import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Page } from '@/components/layout/Page';
import { linkify } from '@/pages/Overview/entities';

/* ── Colors ───────────────────────────────────────────────────────────── */
const C = {
  blueDark: '#274483',
  t1: '#1A1C2E',
  t2: '#5C5F72',
  t3: '#9295A5',
  border: '#E4E0D8',
  warm: '#F5F6F8',
  white: '#FFFFFF',
  pos: '#2A8055',
  neg: '#BC2E3D',
  warn: '#D27A24',
};
const tone = (c?: string) =>
  c === 'positive'
    ? C.pos
    : c === 'warning'
      ? C.warn
      : c === 'negative'
        ? C.neg
        : c === 'muted'
          ? C.t3
          : c === 'text2'
            ? C.t2
            : c === 'blueDark'
              ? C.blueDark
              : C.t1;
const toneBg = (c?: string) =>
  c === 'positive'
    ? 'rgba(42,128,85,.1)'
    : c === 'warning'
      ? 'rgba(210,122,36,.1)'
      : c === 'negative'
        ? 'rgba(188,46,61,.1)'
        : 'rgba(39,68,131,.08)';

/* ── Types ────────────────────────────────────────────────────────────── */
type DetailCol = { value: string; valueColor?: string; l1: string; l2?: string };
type Confidence = { label: string; value: string; l1: string; cta: string; ctaLink: string | null };
type CycleRow = {
  id: string;
  name: string;
  metaPre: string;
  lock: string;
  lockColor: string;
  pct: number;
  barColor: string;
  progress: string;
  note: string;
  status: string;
  statusColor: string;
  details: { intelligenceFed: DetailCol; keySignal: DetailCol; blocking: DetailCol; confidence: Confidence };
};
type Prev = { title: string; locked: string; summary: string; opacity: number };
type CycleData = {
  title: string;
  sub: string;
  headerBadge: string;
  timeline: {
    windowLabel: string;
    windowSub: string;
    todayPct: number;
    todayLabel: string;
    legend: string[];
    start: string;
    end: string;
  };
  rows: CycleRow[];
  previous: Prev[];
};

const card: React.CSSProperties = { background: C.white, border: `1px solid ${C.border}`, borderRadius: 8 };
const eyebrow: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  color: C.t3,
  textTransform: 'uppercase',
  letterSpacing: '.08em',
  marginBottom: 8,
};
const pill = (c: string): React.CSSProperties => ({
  fontSize: 9,
  fontWeight: 700,
  padding: '2px 9px',
  borderRadius: 100,
  background: toneBg(c),
  color: tone(c),
});

function DetailColumn({ label, d }: { label: string; d: DetailCol }) {
  return (
    <div style={{ padding: '14px 18px', borderRight: `1px solid ${C.border}` }}>
      <div style={eyebrow}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: tone(d.valueColor), marginBottom: 2 }}>
        {linkify(d.value)}
      </div>
      <div style={{ fontSize: 10, color: C.t3 }}>{linkify(d.l1)}</div>
      {d.l2 && <div style={{ fontSize: 10, color: C.t3, marginTop: 2 }}>{linkify(d.l2)}</div>}
    </div>
  );
}

export function Cycle() {
  const { data } = useQuery<CycleData>({
    queryKey: ['overview', 'cycle'],
    queryFn: () => fetch('/api/overview/cycle').then((r) => r.json()),
  });
  // Track COLLAPSED rows — default (empty set) means all rows expanded on load.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const tl = data?.timeline;

  return (
    <Page
      title={data?.title ?? 'Q3 Planning Cycle'}
      subheader={data?.sub}
      actions={
        data?.headerBadge ? (
          <div style={{ alignSelf: 'flex-start', marginTop: 6 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '3px 10px',
                borderRadius: 100,
                background: toneBg('negative'),
                color: C.neg,
              }}
            >
              {data.headerBadge}
            </span>
          </div>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3.5">
        {/* Master timeline */}
        {tl && (
          <div style={{ ...card, padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.t2 }}>{tl.windowLabel}</div>
              <div style={{ fontSize: 10, color: C.t3 }}>{tl.windowSub}</div>
            </div>
            <div style={{ position: 'relative', height: 8, background: C.border, borderRadius: 4, marginBottom: 8 }}>
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: '45%',
                  background: C.pos,
                  borderRadius: '4px 0 0 4px',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: '45%',
                  top: 0,
                  height: '100%',
                  width: '30%',
                  background: C.blueDark,
                }}
              />
              <div
                style={{ position: 'absolute', left: '75%', top: 0, height: '100%', width: '4%', background: C.warn }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: `${tl.todayPct}%`,
                  top: -4,
                  height: 16,
                  width: 2,
                  background: C.t1,
                  borderRadius: 1,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 14,
                  left: `${tl.todayPct}%`,
                  transform: 'translateX(-50%)',
                  fontSize: 8.5,
                  fontWeight: 700,
                  color: C.t1,
                  whiteSpace: 'nowrap',
                }}
              >
                {tl.todayLabel}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 18,
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              {tl.legend.map((lg, i) => (
                <div key={lg} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: i === 0 ? C.pos : i === 1 ? C.blueDark : C.warn,
                    }}
                  />
                  <span style={{ fontSize: 9, color: C.t3 }}>{lg}</span>
                </div>
              ))}
              <span style={{ fontSize: 9, color: C.t3 }}>{tl.start}</span>
              <span style={{ fontSize: 9, fontWeight: 700, color: C.neg }}>{tl.end}</span>
            </div>
          </div>
        )}

        {/* Cycle rows */}
        <div className="flex flex-col gap-2.5">
          {(data?.rows ?? []).map((row) => {
            const isOpen = !collapsed.has(row.id);
            return (
              <div key={row.id} style={{ ...card, overflow: 'hidden' }}>
                <div
                  onClick={() => toggle(row.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '220px 1fr auto',
                    alignItems: 'center',
                    padding: '14px 18px',
                    cursor: 'pointer',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: C.t1, marginBottom: 3 }}>{row.name}</div>
                    <div style={{ fontSize: 9.5, color: C.t3 }}>
                      {row.metaPre}
                      <span style={{ fontWeight: 700, color: tone(row.lockColor) }}>{row.lock}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 20px' }}>
                    <div style={{ flex: 1, height: 4, background: C.border, borderRadius: 2, position: 'relative' }}>
                      <div
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${row.pct}%`,
                          background: tone(row.barColor),
                          borderRadius: 2,
                        }}
                      />
                    </div>
                    <div style={{ fontSize: 10.5, color: C.t2, whiteSpace: 'nowrap' }}>{row.progress}</div>
                    <div style={{ fontSize: 10, color: C.t3, whiteSpace: 'nowrap' }}>{linkify(row.note)}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <span style={pill(row.statusColor)}>{row.status}</span>
                    <span
                      style={{
                        fontSize: 11,
                        color: C.t3,
                        transform: isOpen ? 'rotate(180deg)' : 'none',
                        transition: 'transform .15s',
                      }}
                    >
                      ▾
                    </span>
                  </div>
                </div>
                {isOpen && (
                  <div
                    style={{
                      borderTop: `1px solid ${C.border}`,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4,1fr)',
                    }}
                  >
                    <DetailColumn label="Intelligence Fed" d={row.details.intelligenceFed} />
                    <DetailColumn label="Key Signal" d={row.details.keySignal} />
                    <DetailColumn label="What's Blocking" d={row.details.blocking} />
                    <div
                      style={{
                        padding: '14px 18px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <div style={eyebrow}>{row.details.confidence.label}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 2 }}>
                          {row.details.confidence.value}
                        </div>
                        <div style={{ fontSize: 10, color: C.t3 }}>{row.details.confidence.l1}</div>
                      </div>
                      <div style={{ marginTop: 10 }}>
                        {row.details.confidence.ctaLink ? (
                          <Link
                            to={row.details.confidence.ctaLink}
                            style={{ fontSize: 10, fontWeight: 700, color: C.blueDark, textDecoration: 'none' }}
                          >
                            {row.details.confidence.cta}
                          </Link>
                        ) : (
                          <span style={{ fontSize: 10, fontWeight: 700, color: C.blueDark }}>
                            {row.details.confidence.cta}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Previous cycles */}
        <div style={{ ...card, overflow: 'hidden' }}>
          <div
            style={{
              padding: '11px 18px',
              borderBottom: `1px solid ${C.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>Previous Cycles</div>
            <span style={{ fontSize: 10, color: C.t3 }}>Keystone intelligence · historical record</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)' }}>
            {(data?.previous ?? []).map((p, i, arr) => (
              <div
                key={p.title}
                style={{
                  padding: '14px 18px',
                  borderRight: i < arr.length - 1 ? `1px solid ${C.border}` : 'none',
                  opacity: p.opacity,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t1, marginBottom: 4 }}>{p.title}</div>
                <div style={{ fontSize: 10, color: C.t3, marginBottom: 6 }}>{p.locked}</div>
                <div style={{ fontSize: 10.5, color: C.t2, lineHeight: 1.6 }}>{p.summary}</div>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: C.blueDark, marginTop: 8 }}>View decisions →</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Page>
  );
}
